import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../utils/api";
import { healthService } from "../../utils/api";
import { storeAuthData, validateAuthStorage, isAuthenticated } from "../../utils/auth";
import Modal from "../../components/Modal";
import LoadingLogo from "../../components/LoadingLogo";
import "./Login.css";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isServerOffline, setIsServerOffline] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);

  // Ref to track component mount status for safe async state updates
  const isMountedRef = useRef(true);
  // Ref to store timeout ID for cleanup
  const redirectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, []);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Validate email and password to enable/disable the button
  useEffect(() => {
    // Basic email validation regex
    const isEmailValid = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email.toLowerCase());
    const isPasswordNotEmpty = password.length > 0;
    setIsButtonDisabled(!(isEmailValid && isPasswordNotEmpty));
  }, [email, password]);

  // Listen for server status events
  useEffect(() => {
    const handleServerStatus = (event: any) => {
      if (event.detail.status === 'offline') {
        setIsServerOffline(true);
      } else if (event.detail.status === 'online') {
        setIsServerOffline(false);
      }
    };

    window.addEventListener('server-status', handleServerStatus);
    return () => {
      window.removeEventListener('server-status', handleServerStatus);
    };
  }, []);

  // Check if already logged in
  useEffect(() => {
    if (isAuthenticated() && !isServerOffline) {
      console.log("User already authenticated, redirecting to dashboard");
      window.location.href = "/dashboard";
    }
  }, [isServerOffline]);

  const handleLogin = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Prevent multiple submissions
    if (isLoading || isRedirecting) return;

    // Prevent submission if button is disabled due to validation
    if (isButtonDisabled) return;

    setIsLoading(true);
    setError("");

    try {
      // Convert email to lowercase for case-insensitive comparison
      const emailLowercase = email.toLowerCase();
      console.log("Attempting login for:", emailLowercase);

      // This calls our Spring Boot /login endpoint
      const response = await api.post('/login', {
        email: emailLowercase,
        password: password
      }, { skipAuthRedirect: true } as any);

      console.log("Login response received:", response.status);

      // Extract accessToken, refreshToken, and userId from response
      const { accessToken, refreshToken, userId, role } = response.data;

      if (!accessToken || !refreshToken || !userId) {
        throw new Error("Invalid response: missing required authentication data");
      }

      console.log("Received authentication data:", {
        userId,
        role,
        accessTokenLength: accessToken.length,
        refreshTokenLength: refreshToken.length
      });

      // Store authentication data with both tokens
      const authData = storeAuthData(userId, accessToken, refreshToken);

      if (!authData) {
        throw new Error("Failed to store authentication data");
      }

      // Verify the auth data was properly stored
      const isValid = validateAuthStorage();

      if (!isValid) {
        throw new Error("Auth validation failed after storage");
      }

      console.log("Auth storage validated, preparing to navigate");
      setIsRedirecting(true);

      // Use window.location for a full page reload instead of React Router navigation
      // This ensures a clean state and properly initialized auth context
      redirectTimeoutRef.current = setTimeout(() => {
        if (isMountedRef.current) {
          console.log("Performing hard redirect to dashboard...");
          window.location.href = "/dashboard";
        }
      }, 500);

    } catch (err: any) {
      console.error("Login error:", err);
      let errorMessage = "Invalid credentials";

      if (!err.response) {
        // Network error - server is likely offline
        setIsServerOffline(true);
        errorMessage = "Server not responding. Please try again later.";
      } else if (err.response) {
        if (err.response.status === 401) {
          errorMessage = "Incorrect email or password";
        } else if (err.response.status === 403) {
          errorMessage = "Access denied. Please contact your administrator.";
        } else if (err.response.data) {
          errorMessage = err.response.data.message || err.response.data;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      setIsLoading(false);
    }
  };

  const handleForgotPassword = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/forgot-password');
  };

  const handleRetryConnection = () => {
    // Try to check if server is back online using the health service
    healthService.checkServerStatus()
      .then(isOnline => {
        // Guard against state updates on unmounted component
        if (!isMountedRef.current) return;

        if (isOnline) {
          setIsServerOffline(false);
          // If previously authenticated, redirect to dashboard
          if (isAuthenticated()) {
            window.location.href = "/dashboard";
          }
        } else {
          // Server still offline
          setIsServerOffline(true);
        }
      })
      .catch(() => {
        // Guard against state updates on unmounted component
        if (!isMountedRef.current) return;
        // Server still offline
        setIsServerOffline(true);
      });
  };

  if (isRedirecting) {
    return (
      <div className="login-container">
        <div className="login-box">
          <div className="redirecting-message">
            <LoadingLogo text="Redirecting to dashboard..." />
          </div>
        </div>
      </div>
    );
  }



  return (
    <div className="login-container">
      <div className="login-header">
        <img src="/assets/logos/Logo_Parallel.svg" alt="Parallel Logo" className="parallel-logo" />
      </div>
      <div className="login-box">
        <img src="/assets/logos/Logo_Operator_Inline.svg" alt="Parallel Operator" className="logo-operator-inline" />

        <form onSubmit={handleLogin}>
          <div className="input-group">
            <input
              type="text"
              id="email"
              placeholder=" "
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              name="email"
              disabled={isLoading}
            />
            <label htmlFor="email">Email address</label>
          </div>

          <div className="input-group">
            <input
              type={showPassword ? "text" : "password"}
              id="password"
              placeholder=" "
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              name="password"
              disabled={isLoading}
            />
            <label htmlFor="password">Password</label>
            <button
              type="button"
              className="password-toggle-button"
              onClick={togglePasswordVisibility}
              disabled={isLoading}
            >
              <img
                src={showPassword ? "/assets/HidePassword.svg" : "/assets/ShowPassword.svg"}
                alt={showPassword ? "Hide password" : "Show password"}
              />
            </button>
          </div>

          <a href="#" onClick={handleForgotPassword} className="forgot-password">
            Forgot password?
          </a>

          {error && (
            <div className="error">
              <img src="/assets/error.svg" alt="Error" />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            className={`login-button ${isLoading ? 'loading' : ''} ${isButtonDisabled ? 'login-button-disabled' : ''}`}
            disabled={isLoading || isButtonDisabled}
          >
            {isLoading ? (
              "Logging in..."
            ) : (
              <>
                <img src="/assets/Login.svg" alt="Login" className="login-button-icon" />
                Login
              </>
            )}
          </button>
        </form>

        <div className="setup-account">
          Don't have an account? <a href="https://parkwithparallel.com/" className="setup-account-link" target="_blank" rel="noopener noreferrer">Purchase now</a>
        </div>
      </div>

      {/* Server Offline Modal */}
      <Modal
        isOpen={isServerOffline}
        title="Server Offline"
        description="Unable to connect to the server. Please check your internet connection and try again."
        confirmText="Retry"
        cancelText="Cancel"
        onConfirm={handleRetryConnection}
        onCancel={() => setIsServerOffline(false)}
      />
    </div>
  );
};

export default Login;
